// ============================================================
// MultimodalCommandDock — mode selection + route/permission preview
// (v21 first slice).
//
// A collapsible strip above the CommandBar. Selecting a mode chip shows
// what WILL happen (preview), the permission reality, and one primary
// action: navigate (real route), hand off to the CommandBar
// (focus/prefill via the sc-command-focus event — text and voice
// dispatch there is already real), or an honest unavailable
// explanation. The structural no-dead-ends rule lives in dockModel.ts
// and is enforced by a test.
//
// Context tray: the currently selected record (from ShellContext) is
// shown as attachable context. Honest boundary: the tray labels itself
// as context for the preview only — nothing uploads, sends, or
// executes from the dock itself.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShell } from './ShellContext'
import { SHELL_TOKENS } from './styles'
import { DOCK_MODES, type DockMode } from './dockModel'

const COLLAPSE_KEY = 'sc-dock-collapsed'

export const COMMAND_FOCUS_EVENT = 'sc-command-focus'

function readCollapsed(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(COLLAPSE_KEY) !== 'open'
  } catch {
    return true
  }
}

export default function MultimodalCommandDock() {
  const navigate = useNavigate()
  const { selected } = useShell()
  const [collapsed, setCollapsed] = useState(true)
  const [active, setActive] = useState<DockMode | null>(null)

  useEffect(() => { setCollapsed(readCollapsed()) }, [])

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c
      try { window.localStorage.setItem(COLLAPSE_KEY, next ? 'collapsed' : 'open') } catch { /* private mode */ }
      return next
    })
  }, [])

  const runPrimary = useCallback((mode: DockMode) => {
    if (mode.kind === 'route' && mode.route) {
      navigate(mode.route)
      return
    }
    if (mode.kind === 'command_bar') {
      try {
        window.dispatchEvent(new CustomEvent(COMMAND_FOCUS_EVENT, { detail: { prefill: mode.prefill ?? '' } }))
      } catch { /* SSR-safe no-op */ }
    }
    // 'unavailable' has no primary action — its explanation IS the content.
  }, [navigate])

  return (
    <div
      style={{
        borderTop: `1px solid ${SHELL_TOKENS.borderAlt}`,
        background: 'rgba(8,8,12,0.78)',
        backdropFilter: 'blur(14px)',
        padding: collapsed ? '6px 16px' : '10px 16px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={toggle}
          style={{
            border: `1px solid ${SHELL_TOKENS.border}`, background: SHELL_TOKENS.surfaceRaised,
            color: SHELL_TOKENS.inkDim, borderRadius: 6, padding: '4px 9px', cursor: 'pointer',
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
          }}
        >
          {collapsed ? '▲ Multimodal dock' : '▼ Multimodal dock'}
        </button>
        {!collapsed && (
          <span style={{ fontSize: 10.5, color: SHELL_TOKENS.inkWhisper }}>
            Pick a mode — the dock previews the route and permission gate before anything happens. Nothing executes from here.
          </span>
        )}
      </div>

      {!collapsed && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {DOCK_MODES.map((mode) => {
              const isActive = active?.key === mode.key
              const unavailable = mode.kind === 'unavailable'
              return (
                <button
                  key={mode.key}
                  type="button"
                  onClick={() => setActive(isActive ? null : mode)}
                  style={{
                    border: `1px solid ${isActive ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
                    background: isActive ? `${SHELL_TOKENS.brand}1f` : SHELL_TOKENS.surfaceRaised,
                    color: unavailable ? SHELL_TOKENS.inkFaint : isActive ? SHELL_TOKENS.ink : SHELL_TOKENS.inkDim,
                    borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{mode.glyph}</span>
                  {mode.label}
                  {unavailable && <span style={{ fontSize: 8.5, fontFamily: 'var(--font-mono)' }}>· not wired</span>}
                </button>
              )
            })}
          </div>

          {active && (
            <div
              style={{
                marginTop: 10, border: `1px solid ${SHELL_TOKENS.border}`, borderRadius: 10,
                background: SHELL_TOKENS.surface, padding: '11px 13px',
                display: 'grid', gap: 7,
              }}
            >
              <div style={{ fontSize: 12.5, color: SHELL_TOKENS.ink, lineHeight: 1.5 }}>{active.preview}</div>
              <div style={{ fontSize: 11, color: SHELL_TOKENS.inkDim, fontFamily: 'var(--font-mono)' }}>
                gate · {active.permissionNote}
              </div>
              {active.kind === 'route' && (
                <div style={{ fontSize: 11, color: SHELL_TOKENS.inkDim, fontFamily: 'var(--font-mono)' }}>
                  route · {active.route}
                </div>
              )}
              {active.kind === 'unavailable' ? (
                <div style={{ fontSize: 11.5, color: SHELL_TOKENS.inkDim, lineHeight: 1.5, borderTop: `1px dashed ${SHELL_TOKENS.border}`, paddingTop: 7 }}>
                  <strong style={{ color: SHELL_TOKENS.ink }}>Not available yet:</strong> {active.unavailableReason}
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    onClick={() => runPrimary(active)}
                    style={{
                      border: `1px solid ${SHELL_TOKENS.brandLine}`, background: `${SHELL_TOKENS.brand}1f`,
                      color: SHELL_TOKENS.ink, borderRadius: 7, padding: '7px 13px', cursor: 'pointer',
                      fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit',
                    }}
                  >
                    {active.kind === 'route' ? `Open ${active.label}` : 'Focus command bar'}
                  </button>
                </div>
              )}
              {selected !== null && selected !== undefined && (
                <div style={{ fontSize: 10.5, color: SHELL_TOKENS.inkWhisper, fontFamily: 'var(--font-mono)' }}>
                  context tray · 1 selected record attached to this preview (context only — nothing uploads or sends from the dock)
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
